// src/utils/customHooks.ts
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toCanvas, toJpeg } from 'html-to-image';
import { useSaveFlow } from "../context/SaveFlowContext";
import { generatePdfName } from "../utils/filenameUtils";
import { FONT_MAP } from "./fontConfig"; // <--- PAMIĘTAJ O DODANIU TEGO PLIKU

// =====================================================================
//                    GLOBALNY PRELOAD FONTÓW
// =====================================================================
// adjustFontSize w szablonach uzywa canvas.measureText z konkretnym fontem.
// Jesli font nie jest jeszcze zaladowany, canvas spada na sans-serif (wezsze
// metryki), zaniza pomiar i ustawia za duzy rozmiar tekstu. Po dograniu fontu
// tekst nagle skacze do prawidlowej wartosci, a w PDF-ie moze byc obciety.
//
// Rozwiazaniem jest pre-ladowanie WSZYSTKICH fontow zdefiniowanych w FONT_MAP
// gdy tylko user wlaczy jakikolwiek szablon. Hook useSaveTemplate jest
// wywolywany w kazdym szablonie - tu wpinamy preload.
let _fontsPreloaded = false;
const preloadAllCustomFonts = () => {
  if (_fontsPreloaded || typeof document === 'undefined') return;
  _fontsPreloaded = true;
  Object.keys(FONT_MAP).forEach((family) => {
    // .load() startuje pobieranie + dodaje do document.fonts gdy gotowe.
    // .catch ignorujemy - brak pojedynczego fontu nie powinien wywalac procesu.
    document.fonts.load(`1em "${family}"`).catch(() => undefined);
  });
};

// =====================================================================
//                    WYKRYWANIE TRYBU CIEMNEGO PRZEGLĄDARKI
// =====================================================================
// Wymuszony tryb ciemny (np. "Przyciemniaj strony" w Chrome na Androidzie, Samsung Internet)
// potrafi przyciemnić także render html-to-image: białe tło pod przezroczystymi fragmentami
// teł wychodzi czarne, obrazki są przygaszone, teksty rozjaśnione (zgłoszenie: Baśń 41).
// index.html wyłącza go meta color-scheme, ale nie każda przeglądarka to respektuje - więc
// przed zapisem renderujemy białą próbkę tą samą drogą co okładkę i sprawdzamy jej kolor.
const isRenderDarkened = async (): Promise<boolean> => {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;left:-10000px;top:0;width:20px;height:20px;background-color:#ffffff';
  document.body.appendChild(probe);
  try {
    const canvas = await toCanvas(probe, {
      pixelRatio: 1,
      skipFonts: true,
      // klon próbki ma stać w (0,0) obrazka, a nie poza nim jak oryginał
      style: { position: 'static', left: '0', top: '0' },
    });
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const [r, g, b] = Array.from(ctx.getImageData(10, 10, 1, 1).data);
    return r < 230 || g < 230 || b < 230;
  } catch {
    // Próbka się nie wyrenderowała - nie blokujemy zapisu z tego powodu.
    return false;
  } finally {
    probe.remove();
  }
};

const API_URL = "https://cover-generator-api-620522901919.europe-central2.run.app";

interface SaveTemplateParams {
  pdfWidth: string;
  pdfHeight: string;
  pdfNameFromUrl: string | undefined;
}

// Generyczne nazwy CSS, których NIE chcemy traktować jako "używanych fontów"
const GENERIC_FONT_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'inherit',
  'initial',
  'unset',
  'revert',
]);

/**
 * Pobiera unikalne rodziny fontów użyte w elemencie i jego dzieciach
 */
const getUsedFontFamilies = (element: HTMLElement): Set<string> => {
  const usedFonts = new Set<string>();
  const allElements = [element, ...Array.from(element.querySelectorAll('*'))];

  allElements.forEach(el => {
    const style = window.getComputedStyle(el);
    const fontFamily = style.fontFamily;
    if (fontFamily) {
      fontFamily.split(',').forEach(font => {
        const cleaned = font.trim().replace(/['"]/g, '');
        if (cleaned && !GENERIC_FONT_FAMILIES.has(cleaned.toLowerCase())) {
          usedFonts.add(cleaned);
        }
      });
    }
  });

  return usedFonts;
};

/**
 * Mapuje rozszerzenie pliku fontu na poprawny token format() używany w @font-face.
 * Niepoprawny format powoduje, że Chromium odrzuca plik (np. .otf zadeklarowany
 * jako 'truetype' nie zostanie załadowany).
 */
const fontFormatFromUrl = (url: string): string => {
  const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '';
  if (ext === 'woff2') return 'woff2';
  if (ext === 'woff') return 'woff';
  if (ext === 'otf') return 'opentype';
  if (ext === 'ttf') return 'truetype';
  if (ext === 'eot') return 'embedded-opentype';
  if (ext === 'svg') return 'svg';
  return 'truetype';
};

/**
 * Zamienia fonty na Base64. Obsługuje błędy CORS i posiada fallback do mapy plików.
 */
const embedFontsToBase64 = async (targetElement: HTMLElement) => {
  let cssString = '';
  const fontPromises: Promise<void>[] = [];
  const usedFonts = getUsedFontFamilies(targetElement);

      // --- KROK 1: Próba odczytu z załadowanych arkuszy stylów ---
Array.from(document.styleSheets).forEach((sheet) => {
  try {
    // Sprawdzamy dostęp do cssRules. 
    // Jeśli rzuci błędem "Not allowed to access...", przejdzie od razu do catch (e).
    const rules = sheet.cssRules; 
    
    if (!rules) return;

    Array.from(rules).forEach((rule) => {
      if (rule instanceof CSSFontFaceRule) {
        const familyInRule = rule.style.fontFamily.replace(/['"]/g, '');

        if (!usedFonts.has(familyInRule)) return;

        const cssText = rule.cssText;
        const urlMatch = cssText.match(/url\(['"]?([^'")]+)['"]?\)/);
        
        if (urlMatch && urlMatch[1]) {
          let fontUrl = urlMatch[1];
          
          if (fontUrl.startsWith('data:')) {
             cssString += cssText + '\n';
             return;
          }

          if (!fontUrl.startsWith('http')) {
             fontUrl = window.location.origin + '/' + fontUrl.replace(/^(\.\/|\/)/, '');
          }

          const promise = fetch(fontUrl)
            .then(res => res.blob())
            .then(blob => new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            }))
            .then(base64 => {
              cssString += cssText.replace(urlMatch[0], `url('${base64}')`) + '\n';
            })
            .catch(() => { /* błąd fetchowania obsłużymy fallbackiem */ });

          fontPromises.push(promise);
        }
      }
    });
  } catch (e) {
    // TUTAJ WPADA TWÓJ BŁĄD DOMException.
    // Nic nie robimy – po prostu ignorujemy ten konkretny arkusz i idziemy do następnego.
    // Dzięki temu funkcja NIE przerywa działania.
    console.log('Error: ', e);
  }
});

  await Promise.all(fontPromises);

  // --- KROK 2: Inteligentny Fallback dla brakujących fontów (z FONT_MAP) ---
  const fallbackPromises: Promise<void>[] = [];
  
  usedFonts.forEach((fontFamily) => {
    // Jeśli fontu nie ma w cssString (bo np. arkusz był zablokowany), dociągamy go z mapy
    if (!cssString.includes(fontFamily) && FONT_MAP[fontFamily]) {
      const fontUrl = FONT_MAP[fontFamily];
      
      const p = fetch(fontUrl)
        .then(res => res.blob())
        .then(blob => new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        }))
        .then(base64 => {
          const format = fontFormatFromUrl(fontUrl);
          // Dodajemy definicję ręcznie. font-style ustawiamy na normal (uniwersalnie dla plików public)
          cssString += `
            @font-face {
              font-family: '${fontFamily}';
              src: url('${base64}') format('${format}');
              font-weight: normal;
              font-style: normal;
            }\n`;
          console.log(`Fallback: Pomyślnie doładowano font ${fontFamily} z pliku (format: ${format}).`);
        })
        .catch(err => console.warn(`Nie udało się doładować fontu ${fontFamily} nawet z fallbacku:`, err));
      
      fallbackPromises.push(p);
    }
  });

  await Promise.all(fallbackPromises);
  return cssString;
};

const useSaveTemplate = () => {
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { promptForUserData } = useSaveFlow();

  // Kickujemy preload wszystkich fontów na pierwszym mountcie szablonu.
  // Dzieki temu adjustFontSize w szablonach ma juz dostepne metryki fontu
  // zanim canvas.measureText zostanie pierwszy raz wywolany.
  useEffect(() => { preloadAllCustomFonts(); }, []);

  const saveTemplate = async (
    contentElementId: string,
    { pdfWidth, pdfHeight }: SaveTemplateParams,
    croppedImage: string | null,
    requireImage: boolean = true
  ) => {
    if (requireImage && !croppedImage) {
      alert("Proszę wgrać i wykadrować zdjęcie przed zapisem.");
      return;
    }

    if (await isRenderDarkened()) {
      alert(
        "Twoja przeglądarka ma włączony tryb ciemny dla stron, który zmienia kolory okładki " +
        "(białe tło wychodzi czarne). Wyłącz tryb ciemny / przyciemnianie stron w ustawieniach " +
        "przeglądarki i zapisz okładkę ponownie."
      );
      return;
    }

    const userData = await promptForUserData();
    if (!userData) return;

    const currentPath = location.pathname.split('/')[1] || "szablon";
    const filename = generatePdfName(userData.fullName, currentPath);

    try {
      setIsSaving(true);
      const contentElement = document.getElementById(contentElementId);
      if (!contentElement) throw new Error("Element nie istnieje");

      // Czekamy na gotowość systemową fontów
      await document.fonts.ready;
      const usedFonts = getUsedFontFamilies(contentElement);

      // Wstępne "rozgrzanie" fontów w przeglądarce.
      // UWAGA: NIE wolno tu używać Promise.race z timeoutem, bo jeśli font nie zdąży się załadować,
      // toJpeg renderuje obrazek z fallbackiem (np. Garamondem) i PDF wychodzi z błędną czcionką.
      // Dajemy fontom maksymalnie 8s na załadowanie (np. duży WOFF/OTF na wolnej sieci).
      const loadPromises = Array.from(usedFonts).map(font =>
        document.fonts.load(`1em "${font}"`).catch(err =>
          console.warn(`Nie udało się załadować fontu ${font}:`, err)
        )
      );
      await Promise.race([
        Promise.all(loadPromises),
        new Promise(resolve => setTimeout(resolve, 8000))
      ]);

      const base64FontsCSS = await embedFontsToBase64(contentElement);

      // Dodatkowe oczekiwanie na pełen render (np. <img src="data:..." /> z wgranym zdjęciem)
      // Dwa kolejne requestAnimationFrame gwarantują, że layout i paint są ukończone.
      await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

      // Czekamy aż wszystkie obrazy w środku (w tym data: URL ze zdjęciem użytkownika) są zdekodowane.
      const imgs = Array.from(contentElement.querySelectorAll('img')) as HTMLImageElement[];
      await Promise.all(
        imgs.map(img => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          if (typeof img.decode === 'function') {
            return img.decode().catch(() => undefined);
          }
          return new Promise<void>(resolve => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          });
        })
      );

      const dataUrl = await toJpeg(contentElement, {
        quality: 1.0,
        pixelRatio: 3,
        skipFonts: false, // Ustawiamy na false, by html-to-image wspomógł rendering
        fontEmbedCSS: base64FontsCSS,
        style: { margin: '0' },
        // cacheBust: true psuje data: URL-e (dokleja ?t=... do data:image/... i obraz znika).
        // Zostawiamy false, żeby zdjęcie wgrane przez użytkownika trafiło do PDF-a.
        cacheBust: false,
        // KRYTYCZNE dla JPEG: jezeli html-to-image renderuje cokolwiek z przezroczystoscia
        // (np. SVG z transparentnymi obszarami, albo element ktorego rendering czesciowo padl),
        // JPEG zamienia transparencje na CZARNY domyslny kolor canvasa. Wymuszamy biale tlo,
        // zeby ewentualne ubytki w renderze byly biale (= reszta projektu), a nie czarne.
        backgroundColor: '#ffffff',
      });

      const htmlContent = `
        <html>
        <head>
          <style>
            body, html { margin: 0; padding: 0; width: 100%; height: 100%; }
            img { width: 100%; height: 100%; object-fit: contain; }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" />
        </body>
        </html>`;

      const response = await fetch(`${API_URL}/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          htmlContent,
          pdfWidth,
          pdfHeight,
          filename,
          userImage: croppedImage
        }),
      });

      if (response.ok) {
        navigate("/thank-you");
      } else {
        throw new Error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Wystąpił błąd podczas generowania. Spróbuj ponownie.");
    } finally {
      setIsSaving(false);
    }
  };

  return { saveTemplate, isSaving };
};

export default useSaveTemplate;
